import { useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useReturnFocus } from '../hooks/useReturnFocus';
import { useTranslation } from '../i18n/useTranslation';
import { useAquariums } from '../hooks/useAquariums';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { Button } from '../components/atoms/Button';
import {
  DashboardHero,
  MetricCard,
} from '../components/reef';
import { ReefInsightCard } from '../components/insights';
import type { MetricStatus, MetricTrend } from '../components/reef';
import { TaskSummaryCard } from '../components/dashboard/TaskSummaryCard';
import { NewTankSection } from '../components/maturity/NewTankSection';
import { TodayCard } from '../components/dashboard/TodayCard';
import { EquipmentSafetyNotice } from '../components/equipmentSafety/EquipmentSafetyNotice';
import { KhUsageCard } from '../components/dashboard/KhUsageCard';
import { DashboardTimeline } from '../components/timeline/DashboardTimeline';
import { useSelectedAquarium } from '../hooks/useSelectedAquarium';
import { deriveStatus, parameterIdFor, useLatestMeasurements } from '../hooks/useLatestMeasurements';
import { useLatestProbeValues } from '../hooks/useController';
import type { ProbeValue } from '../api/controller';
import type { MeasurementStatus, ParameterMeasurement } from '../hooks/useLatestMeasurements';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { DASHBOARD_CARDS_ANCHOR } from '../components/settings/DashboardCardsSection';
import { computeHealthScore } from '../utils/healthScore';
import { DEFAULT_DASHBOARD_PARAMETERS, dashboardCardLabel, useDashboardParameters } from '../components/dashboard/dashboardCards';
import { parameterStatusKey } from '../components/measurements/measurementHistoryUtils';
import './DashboardPage.css';

function measurementStatusToMetricStatus(status: MeasurementStatus): MetricStatus {
  if (status === 'unknown') return 'stable';
  return status;
}

/** Decimal places of a number as entered (7 → 0, 6.7 → 1), capped at 3. */
function decimalsOf(value: number): number {
  const text = String(value);
  const dot = text.indexOf('.');
  return dot < 0 || text.includes('e') ? 0 : Math.min(3, text.length - dot - 1);
}

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * Value text of a tile plus its trend (release 1.5.0, 3b). With a previous
 * value both numbers use the same decimals, so "7,0" reads against "6,7".
 * No trend (no arrow) without a previous, comparable value.
 */
function tileTexts(
  measurement: ParameterMeasurement,
  language: string,
  t: TFunction,
): { value: string; trend?: MetricTrend } {
  const { previous, trend } = measurement;
  if (previous == null || trend == null) {
    return { value: new Intl.NumberFormat(language, { maximumFractionDigits: 3 }).format(measurement.value) };
  }
  const digits = Math.max(decimalsOf(measurement.value), decimalsOf(previous.value));
  const format = new Intl.NumberFormat(language, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const value = format.format(measurement.value);
  const from = format.format(previous.value);
  const to = measurement.unit ? `${value} ${measurement.unit}` : value;
  return {
    value,
    trend: {
      direction: trend.direction,
      previousText: t('dashboard.trend.previous', { value: from }),
      srText: t(`dashboard.trend.${trend.direction}`, { from, to }),
    },
  };
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="rg-dashboard-page__state">
      <div className="rg-dashboard-page__empty-card">
        <div className="rg-dashboard-page__empty-icon" aria-hidden="true">🐠</div>
        <Heading level={2}>{t('dashboard.empty.heading')}</Heading>
        <Text>{t('dashboard.empty.description')}</Text>
        <Link to="/aquariums/new" className="rg-dashboard-page__empty-cta">
          <span aria-hidden="true">+</span>
          {t('dashboard.empty.cta')}
        </Link>
      </div>
    </div>
  );
}

function MetricSkeleton() {
  return <article className="rg-metric-card rg-metric-card--skeleton" aria-hidden="true" />;
}

/** Focus returns here after the test session (TestSessionPage passes focusElementId back). */
const LOG_TESTS_BUTTON_ID = 'dashboard-log-tests';

/** Health score of the default parameters (not of the user's card choice). */
function useHealthScore(aquariumId: string | undefined) {
  const { isLoading, data } = useLatestMeasurements(aquariumId, DEFAULT_DASHBOARD_PARAMETERS);
  return useMemo(() => (isLoading ? undefined : computeHealthScore(data, DEFAULT_DASHBOARD_PARAMETERS)), [isLoading, data]);
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  useReturnFocus();
  const { data: aquariums, isLoading, error, refetch } = useAquariums();

  const { selectedAquariumId } = useSelectedAquarium(aquariums ?? []);
  const selectedAquarium = (aquariums ?? []).find((a) => a.id === selectedAquariumId) ?? aquariums?.[0];

  // The cards the user picked in Settings → Dashboard (else the default set). The
  // health score always rates the default set, so it stays comparable between users.
  const cards = useDashboardParameters();
  const {
    isLoading: latestLoading,
    error: metricsError,
    data: metricsData,
    refetch: refetchMetrics,
  } = useLatestMeasurements(selectedAquarium?.id, cards.parameters);
  const metricsLoading = latestLoading || cards.isLoading;
  const health = useHealthScore(selectedAquarium?.id);
  // A card shows the newer of the latest measurement and the tank probe of a
  // connected controller (#1899) — e.g. a KH Director that tests more often than
  // the hand test; redox, oxygen or a parameter never tested come from the probe.
  const needsProbe = !metricsLoading && metricsError == null && cards.parameters.length > 0;
  const probeValues = useLatestProbeValues(selectedAquarium?.id, needsProbe).data;
  const probeFor = (param: string): ProbeValue | undefined => {
    const probe = probeValues?.find((p) => p.parameterId === parameterIdFor(param));
    const measurement = metricsData[param];
    if (!probe) return undefined;
    return measurement == null || Date.parse(probe.measuredAt) > Date.parse(measurement.timestamp) ? probe : undefined;
  };
  // Names of ICP elements and other values without a translation come with the data; probe-only values have their own.
  const labelFor = (param: string): string => {
    const probe = metricsData[param] == null ? probeFor(param) : undefined;
    return dashboardCardLabel(t, param, metricsData[param]?.parameterName ?? (probe ? String(t(`controller.parameter.${probe.parameterId}`)) : undefined));
  };

  // Deep links like /dashboard#reef-intelligence (timeline AI-insight events)
  // scroll to and focus the card once it is rendered.
  const hasAquariums = !isLoading && aquariums != null && aquariums.length > 0;
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!hasAquariums || id === '') return;
    const target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView?.({ block: 'start' });
    target.focus({ preventScroll: true });
  }, [location.hash, hasAquariums]);

  const handleLogTests = (): void => {
    if (selectedAquarium?.id == null) return;
    navigate(`/aquariums/${selectedAquarium.id}/measurements/session`, {
      state: { returnTo: '/dashboard', invokerElementId: LOG_TESTS_BUTTON_ID },
    });
  };

  const content = (
    <PageContent width="wide">
      <PageHeader
        title={t('dashboard.title')}
        actions={
          hasAquariums ? (
            <Button
              id={LOG_TESTS_BUTTON_ID}
              variant="primary"
              onClick={handleLogTests}
              disabled={selectedAquarium?.id == null}
            >
              {t('dashboard.logTests.cta')}
            </Button>
          ) : undefined
        }
      />
      {isLoading && (
        <LiveRegion busy className="rg-dashboard-page__state">
          <Text>{t('dashboard.loading')}</Text>
        </LiveRegion>
      )}

      {!isLoading && error != null && (
        <LoadErrorState
          title={t('dashboard.loadErrorTitle')}
          message={t('dashboard.loadErrorMessage')}
          onRetry={() => refetch?.()}
          live="polite"
        />
      )}

      {!isLoading && error == null && (aquariums == null || aquariums.length === 0) && (
        <EmptyState />
      )}

      {!isLoading && error == null && aquariums != null && aquariums.length > 0 && (
        <>
          <DashboardHero
            aquariumId={selectedAquarium?.id}
            aquariumName={selectedAquarium?.name}
            aquariumSubtitle={selectedAquarium?.description ?? undefined}
            health={health}
            healthUnavailable={metricsError != null}
          />

          {selectedAquarium?.id != null && <TodayCard aquariumId={selectedAquarium.id} />}

          {selectedAquarium?.id != null && <NewTankSection aquariumId={selectedAquarium.id} />}
          {selectedAquarium?.id != null && <EquipmentSafetyNotice aquariumId={selectedAquarium.id} />}

          <Section
            title={t('dashboard.waterParameters')}
            actions={
              <Button variant="ghost" size="sm" onClick={() => navigate(`/settings#${DASHBOARD_CARDS_ANCHOR}`)}>
                {t('dashboard.chooseCards')}
              </Button>
            }
          >
          {metricsLoading && (
            <LiveRegion visuallyHidden busy>{t('dashboard.metricsLoading')}</LiveRegion>
          )}
          {!metricsLoading && metricsError != null ? (
            <LoadErrorState
              compact
              title={t('dashboard.metricsError')}
              live="polite"
              onRetry={() => refetchMetrics?.()}
            />
          ) : (
            <div className="rg-dashboard-page__metrics">
              {metricsLoading && cards.parameters.map((param) => (
                <MetricSkeleton key={param} />
              ))}

              {!metricsLoading && cards.parameters.map((param) => {
                const measurement = metricsData[param];
                const probe = probeFor(param);
                if (probe) {
                  const status = deriveStatus(probe.target, probe.value);
                  const statusKey = parameterStatusKey(status);
                  return (
                    <MetricCard
                      key={param}
                      label={labelFor(param)}
                      value={new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(probe.value)}
                      unit={probe.unit ?? undefined}
                      status={measurementStatusToMetricStatus(status)}
                      statusLabel={statusKey === null ? '—' : t(statusKey)}
                      source={t('dashboard.probeSource', { origin: probe.origin ?? t('controller.probe.controller') })}
                    />
                  );
                }
                if (measurement == null) {
                  return (
                    <MetricCard
                      key={param}
                      label={labelFor(param)}
                      value="—"
                      status="stable"
                      statusLabel={t('dashboard.metricStatus.noData')}
                    />
                  );
                }
                const statusKey = parameterStatusKey(measurement.status);
                const texts = tileTexts(measurement, i18n.language, t);
                return (
                  <MetricCard
                    key={param}
                    label={labelFor(param)}
                    value={texts.value}
                    unit={measurement.unit ?? undefined}
                    status={measurementStatusToMetricStatus(measurement.status)}
                    statusLabel={statusKey === null ? '—' : t(statusKey)}
                    trend={texts.trend}
                    source={measurement.source === 'controller' ? t('dashboard.controllerSource') : undefined}
                  />
                );
              })}
            </div>
          )}
          </Section>

          <Section title={t('dashboard.overview')}>
          <div className="rg-dashboard-page__grid">
            <div className="rg-dashboard-page__main-column">
              <ReefInsightCard aquariumId={selectedAquarium?.id} />
            </div>
            <div className="rg-dashboard-page__side-column">
              <TaskSummaryCard aquariumId={selectedAquarium?.id} />
              <KhUsageCard aquariumId={selectedAquarium?.id} />
              {selectedAquarium != null && (
                <DashboardTimeline aquariumId={selectedAquarium.id} />
              )}
            </div>
          </div>
          </Section>
        </>
      )}
    </PageContent>
  );

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={content}
    />
  );
}

export default DashboardPage;
